import { useState, useEffect } from 'react'
import { Input } from './Input'
import { Button } from './Button'
import { Alert } from './Alert'
import { usePaymentService, Payment } from '../services/paymentService'

interface ClientPaymentsProps {
  clientId: string
  // When true, skips the collapse/expand toggle and shows the full payments
  // view immediately — used on the dedicated Payments tab. Pair with
  // `key={clientId}` at the call site so switching clients remounts cleanly.
  hideToggle?: boolean
}

const formatAmount = (cents: number) => `$${(cents / 100).toFixed(2)}`

const formatDate = (value?: string | null) => {
  if (!value) return null
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return value
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
}

const statusClasses = (status?: string) => {
  switch (status) {
    case 'succeeded':
    case 'paid':
    case 'completed':
      return 'bg-emerald-100 text-emerald-700'
    case 'failed':
    case 'declined':
      return 'bg-red-100 text-red-700'
    default:
      return 'bg-amber-100 text-amber-700'
  }
}

export const ClientPayments: React.FC<ClientPaymentsProps> = ({ clientId, hideToggle = false }) => {
  const [expanded, setExpanded] = useState(hideToggle)
  const [payments, setPayments] = useState<Payment[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [amount, setAmount] = useState('50.00')
  const [description, setDescription] = useState('Invoice payment')
  const [submitting, setSubmitting] = useState(false)
  const { listPayments, createPayment } = usePaymentService()

  const load = async () => {
    setLoading(true)
    setError(null)
    try {
      const results = await listPayments(clientId)
      setPayments(results)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load payments')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (hideToggle) {
      load()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const toggle = () => {
    const next = !expanded
    setExpanded(next)
    if (next) {
      load()
    }
  }

  const handleTakePayment = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    const amountCents = Math.round(Number(amount) * 100)
    if (!amountCents || amountCents <= 0) {
      setError('Enter a valid amount')
      return
    }

    setSubmitting(true)
    try {
      const payment = await createPayment(clientId, {
        amount: amountCents,
        description,
      })
      setPayments((prev) => [payment, ...prev])
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to take payment')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className={hideToggle ? '' : 'mt-2'}>
      {!hideToggle && (
        <button
          type="button"
          onClick={toggle}
          className="text-sm text-accent hover:opacity-80 font-medium"
        >
          {expanded ? 'Hide Payments' : 'Payments'}
        </button>
      )}

      {expanded && (
        <div className={hideToggle ? '' : 'mt-3 p-4 bg-gray-50 rounded-lg border border-gray-200'}>
          {error && <Alert type="error" className="mb-3">{error}</Alert>}

          <form onSubmit={handleTakePayment} className="flex flex-wrap gap-2 items-end mb-5 p-3 bg-white rounded-lg border border-gray-200">
            <div className="w-32">
              <Input label="Amount (AUD)" type="number" step="0.01" min="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} />
            </div>
            <div className="flex-1 min-w-[160px]">
              <Input label="Description" value={description} onChange={(e) => setDescription(e.target.value)} />
            </div>
            <Button type="submit" size="sm" disabled={submitting}>
              {submitting ? 'Charging...' : 'Take Payment'}
            </Button>
          </form>

          <h4 className="text-sm font-semibold text-gray-900 mb-2">Payment History</h4>
          {loading ? (
            <p className="text-sm text-gray-600">Loading...</p>
          ) : payments.length === 0 ? (
            <p className="text-sm text-gray-600">No payments yet</p>
          ) : (
            <div className="divide-y divide-gray-200 bg-white rounded-lg border border-gray-200">
              {payments.map((p) => (
                <div key={p.id} className="flex items-center justify-between gap-3 px-3 py-2.5">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{p.description || 'Payment'}</p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {formatDate(p.transactionDate) || formatDate(p.createdAt)}
                    </p>
                  </div>
                  <div className="shrink-0 text-right">
                    <p className="text-sm font-semibold text-gray-900">{formatAmount(p.amount)}</p>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium mt-0.5 ${statusClasses(p.status)}`}>
                      {p.status || 'pending'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
